import { __assign, __extends, __rest } from "tslib";
import * as React from "react";
import { View, Animated, TextInput as NativeTextInput, StyleSheet, I18nManager, Platform } from "react-native";
import color from "color";
import { black } from '../styles/colors';
import InputLabel from "./Label/InputLabel";
import configureFonts from "../styles/fonts";
import { MAXIMIZED_LABEL_FONT_SIZE, MINIMIZED_LABEL_FONT_SIZE, LABEL_WIGGLE_X_OFFSET, LABEL_PADDING_HORIZONTAL } from "./constants";
import { calculateLabelTopPosition, calculateInputHeight, calculatePadding, adjustPaddingFlat, interpolatePlaceholder } from "./helpers";
import { textStyle } from "../v2/theme";
var fonts = configureFonts();
var MINIMIZED_LABEL_Y_OFFSET = -18;
var LABEL_PADDING_TOP = 30;
var LABEL_PADDING_TOP_DENSE = 24;
var MIN_HEIGHT = 64;
var MIN_DENSE_HEIGHT_WL = 52;
var MIN_DENSE_HEIGHT = 40;
var INPUT_OFFSET = 8;
var PnmacTextInputFlat = /** @class */function (_super) {
  __extends(PnmacTextInputFlat, _super);
  function PnmacTextInputFlat() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  PnmacTextInputFlat.prototype.render = function () {
    var _a = this.props,
      disabled = _a.disabled,
      editable = _a.editable,
      label = _a.label,
      error = _a.error,
      selectionColor = _a.selectionColor,
      underlineColor = _a.underlineColor,
      dense = _a.dense,
      style = _a.style,
      theme = _a.theme,
      render = _a.render,
      multiline = _a.multiline,
      parentState = _a.parentState,
      innerRef = _a.innerRef,
      onFocus = _a.onFocus,
      onBlur = _a.onBlur,
      onChangeText = _a.onChangeText,
      onLayoutAnimatedText = _a.onLayoutAnimatedText,
      noUnderline = _a.noUnderline,
      accessibilityLabel = _a.accessibilityLabel,
      rest = __rest(_a, ["disabled", "editable", "label", "error", "selectionColor", "underlineColor", "dense", "style", "theme", "render", "multiline", "parentState", "innerRef", "onFocus", "onBlur", "onChangeText", "onLayoutAnimatedText", "noUnderline", "accessibilityLabel"]);
    var font = fonts.regular;
    var hasActiveOutline = parentState.focused || error;
    var _b = StyleSheet.flatten(style) || {},
      fontSizeStyle = _b.fontSize,
      fontWeight = _b.fontWeight,
      height = _b.height,
      paddingHorizontal = _b.paddingHorizontal,
      viewStyle = __rest(_b, ["fontSize", "fontWeight", "height", "paddingHorizontal"]);
    var fontSize = fontSizeStyle || MAXIMIZED_LABEL_FONT_SIZE;
    var paddingOffset = paddingHorizontal !== undefined && typeof paddingHorizontal === "number" ? {
      paddingHorizontal: paddingHorizontal
    } : StyleSheet.flatten(styles.paddingOffset);
    var inputTextColor, activeColor, underlineColorCustom, placeholderColor, errorColor;
    if (disabled) {
      inputTextColor = activeColor = color("#1B1B1B").alpha(0.54).rgb().string();
      placeholderColor = color('#bdbdbd').alpha(0.26).rgb().string();
      underlineColorCustom = "transparent";
    } else {
      inputTextColor = "#1B1B1B";
      activeColor = error ? "#B00020" : "#1B1B1B";
      placeholderColor = color(black).alpha(0.54).rgb().string();
      errorColor = "#B00020";
      underlineColorCustom = underlineColor || color('#bdbdbd').alpha(0.26).rgb().string();
    }
    var containerStyle = {
      backgroundColor: color('#FFF'),
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10
    };
    var labelScale = MINIMIZED_LABEL_FONT_SIZE / fontSize;
    var fontScale = MAXIMIZED_LABEL_FONT_SIZE / fontSize;
    var labelWidth = parentState.labelLayout.width;
    var labelHeight = parentState.labelLayout.height;
    var labelHalfWidth = labelWidth / 2;
    var labelHalfHeight = labelHeight / 2;
    var baseLabelTranslateX = (I18nManager.isRTL ? 1 : -1) * (labelHalfWidth - labelScale * labelWidth / 2) + (1 - labelScale) * paddingOffset.paddingHorizontal;
    var minInputHeight = dense ? (label ? MIN_DENSE_HEIGHT_WL : MIN_DENSE_HEIGHT) - LABEL_PADDING_TOP_DENSE : MIN_HEIGHT - LABEL_PADDING_TOP;
    var inputHeight = calculateInputHeight(labelHeight, height, minInputHeight);
    var topPosition = calculateLabelTopPosition(labelHeight, inputHeight, multiline && height ? 0 : !height ? minInputHeight / 2 : 0);
    if (height && typeof height !== "number")
      // eslint-disable-next-line
      console.warn("Currently we support only numbers in height prop");
    var paddingSettings = {
      height: height ? +height : null,
      labelHalfHeight: labelHalfHeight,
      offset: INPUT_OFFSET,
      multiline: multiline ? multiline : null,
      dense: dense ? dense : null,
      topPosition: topPosition,
      fontSize: fontSize,
      label: label,
      scale: fontScale,
      isAndroid: Platform.OS === "android",
      styles: StyleSheet.flatten(dense ? styles.inputFlatDense : styles.inputFlat)
    };
    var pad = calculatePadding(paddingSettings);
    var paddingFlat = adjustPaddingFlat(__assign(__assign({}, paddingSettings), {
      pad: pad
    }));
    var baseLabelTranslateY = -labelHalfHeight - (topPosition + MINIMIZED_LABEL_Y_OFFSET);
    var placeholderOpacity = hasActiveOutline ? interpolatePlaceholder(parentState.labeled, hasActiveOutline) : parentState.labelLayout.measured ? 1 : 0;
    var labelProps = {
      label: label,
      onLayoutAnimatedText: onLayoutAnimatedText,
      placeholderOpacity: placeholderOpacity,
      error: error,
      placeholderStyle: styles.placeholder,
      baseLabelTranslateY: baseLabelTranslateY,
      baseLabelTranslateX: baseLabelTranslateX,
      font: font,
      fontSize: fontSize,
      fontWeight: fontWeight,
      labelScale: labelScale,
      wiggleOffsetX: LABEL_WIGGLE_X_OFFSET,
      topPosition: topPosition,
      paddingOffset: paddingOffset,
      hasActiveOutline: hasActiveOutline,
      activeColor: activeColor,
      placeholderColor: placeholderColor,
      errorColor: errorColor
    };
    var minHeight = height || (dense ? label ? MIN_DENSE_HEIGHT_WL : MIN_DENSE_HEIGHT : MIN_HEIGHT);
    var flatHeight = inputHeight + (!height ? dense ? LABEL_PADDING_TOP_DENSE : LABEL_PADDING_TOP : 0);
    return /*#__PURE__*/React.createElement(View, {
      style: [containerStyle, viewStyle],
      nativeID: rest.idTag1
    }, !noUnderline ? /*#__PURE__*/React.createElement(Underline, {
      parentState: parentState,
      underlineColorCustom: underlineColorCustom,
      error: error,
      activeColor: activeColor,
      nativeID: rest.idTag2
    }) : null, /*#__PURE__*/React.createElement(View, {
      style: {
        paddingTop: 0,
        paddingBottom: 0,
        minHeight: minHeight
      },
      nativeID: rest.idTag3
    }, /*#__PURE__*/React.createElement(InputLabel, {
      parentState: parentState,
      labelProps: labelProps,
      nativeID: rest.idTag4
    }), render === null || render === void 0 ? void 0 : render(__assign(__assign({}, rest), {
      accessibilityLabel: accessibilityLabel,
      ref: innerRef,
      onChangeText: onChangeText,
      // @ts-ignore
      placeholder: label ? parentState.placeholder : this.props.placeholder,
      placeholderTextColor: placeholderColor,
      editable: !disabled && editable,
      selectionColor: typeof selectionColor === "undefined" ? activeColor : selectionColor,
      onFocus: onFocus,
      onBlur: onBlur,
      underlineColorAndroid: "transparent",
      multiline: multiline,
      style: [styles.input, paddingOffset, !multiline || multiline && height ? {
        height: flatHeight
      } : {}, paddingFlat, __assign(__assign({}, textStyle.formTextInput), {
        color: inputTextColor,
        textAlignVertical: multiline ? "top" : "center"
      })]
    }))));
  };
  PnmacTextInputFlat.defaultProps = {
    disabled: false,
    error: false,
    multiline: false,
    editable: true,
    render: function (props) {
      return /*#__PURE__*/React.createElement(View, null, props.left && props.left(), /*#__PURE__*/React.createElement(NativeTextInput, __assign({
        accessibilityLabel: props.accessibilityLabel
      }, props)), props.right && props.right());
    }
  };
  return PnmacTextInputFlat;
}(React.Component);
export default /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(PnmacTextInputFlat, __assign({
    innerRef: ref
  }, props));
});
var Underline = function (_a) {
  var parentState = _a.parentState,
    error = _a.error,
    activeColor = _a.activeColor,
    underlineColorCustom = _a.underlineColorCustom;
  var backgroundColor = parentState.focused ? activeColor : underlineColorCustom;
  if (error) backgroundColor = "#B00020";
  return /*#__PURE__*/React.createElement(Animated.View, {
    style: [styles.underline, {
      backgroundColor: backgroundColor,
      transform: [{
        scaleY: parentState.focused ? 1 : 0.5
      }]
    }]
  });
};
var styles = StyleSheet.create({
  placeholder: {
    position: "absolute",
    left: 0
  },
  underline: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 1
  },
  input: {
    flexGrow: 1,
    margin: 0,
    textAlign: I18nManager.isRTL ? "right" : "left",
    zIndex: 1
  },
  inputFlat: {
    paddingTop: 42,
    paddingBottom: 4
  },
  inputFlatDense: {
    paddingTop: 22,
    paddingBottom: 2
  },
  paddingOffset: {
    paddingHorizontal: 0
  }
});